import { isPublicSimulationRuntime, publicSimulationSaasContext } from './publicSimulationRuntime';
import axios from 'axios';
import msalConfig, { getActiveConfig } from '@/authConfig';
import type { AuthContextResponse } from '@/types/auth_context';
import { getAuthContextState } from '@/uptk/core/api/auth_context_store';
import { getTenantOverrideState } from './tenant_override_store';

const DEFAULT_WORKSPACE_ID = 'default';

type ResolveOptions = {
  tenantId?: string;
  organizationId?: string;
  workspaceId?: string;
  authContext?: AuthContextResponse | null;
};

export type SaasContext = {
  tenantId: string;
  organizationId: string;
  workspaceId: string;
};

const normalize = (value?: string | null) => {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }
  const lowered = trimmed.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') {
    return '';
  }
  return trimmed;
};

const readStorageValue = (key: string) => {
  if (typeof window === 'undefined') {
    return '';
  }
  return (localStorage.getItem(key) ?? '').trim();
};

const readFirstStorageValue = (...keys: string[]) =>
  keys.map((key) => readStorageValue(key)).find((value) => value.length > 0) ?? '';

const isBrowserLocalhost = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(host)
  );
};

const firstNonEmpty = (...values: Array<string | null | undefined>) =>
  values.map(normalize).find((value) => value.length > 0) ?? '';

/**
 * Resolves SaaS context from provided options.
 * Priority order:
 * explicit options > backend auth context > config fallback > hard default.
 */
export const resolveSaasContext = (options: ResolveOptions = {}): SaasContext => {
  if (isPublicSimulationRuntime()) return publicSimulationSaasContext;
  const authContext = options.authContext ?? getAuthContextState();
  const tenantOverride = getTenantOverrideState();
  const activeConfig = getActiveConfig();
  const configTenant = activeConfig?.auth?.tenantId;
  const configWorkspace = activeConfig?.auth?.workspaceIds?.[0]?.id;
  const localConfigTenant = isBrowserLocalhost() ? msalConfig?.local?.auth?.tenantId : '';
  const localConfigWorkspace = isBrowserLocalhost()
    ? msalConfig?.local?.auth?.workspaceIds?.[0]?.id
    : '';
  const storageTenant = readFirstStorageValue('tenantid', 'tenantId');
  const storageOrganization = readFirstStorageValue('organizationid', 'organizationId');
  const storageWorkspace = readFirstStorageValue(
    'workspaceId',
    'workspaceid',
    'subscriptionId',
    'subscriptionid'
  );
  const overrideTenant = tenantOverride?.tenantId;
  const overrideOrganization = tenantOverride?.organizationId;

  const tenantId = firstNonEmpty(
    options.tenantId,
    overrideTenant,
    authContext?.tenantId,
    storageTenant,
    configTenant,
    localConfigTenant
  );
  const organizationId = firstNonEmpty(
    options.organizationId,
    overrideOrganization,
    authContext?.organizationId,
    storageOrganization,
    options.tenantId,
    overrideTenant,
    authContext?.tenantId,
    storageTenant,
    tenantId
  );
  const workspaceId = firstNonEmpty(
    options.workspaceId,
    authContext?.workspaceId,
    storageWorkspace,
    configWorkspace,
    localConfigWorkspace,
    DEFAULT_WORKSPACE_ID
  );

  return { tenantId, organizationId, workspaceId };
};

/**
 * @deprecated Context should come from backend auth context.
 * Kept as compatibility shim while legacy modules still read localStorage.
 */
export const persistSaasContext = (context: SaasContext) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (context.tenantId) {
    localStorage.setItem('tenantid', context.tenantId);
    localStorage.setItem('tenantId', context.tenantId);
  }
  if (context.organizationId) {
    localStorage.setItem('organizationid', context.organizationId);
    localStorage.setItem('organizationId', context.organizationId);
  }
  if (context.workspaceId) {
    localStorage.setItem('workspaceId', context.workspaceId);
  }
};

export const applySaasHeaders = (context: SaasContext) => {
  const applyHeader = (key: string, value: string) => {
    if (value && value.trim().length > 0) {
      axios.defaults.headers.common[key] = value;
    } else {
      delete axios.defaults.headers.common[key];
    }
  };

  applyHeader('tenantid', context.tenantId);
  applyHeader('tenantId', context.tenantId);
  applyHeader('organizationid', context.organizationId);
  applyHeader('organizationId', context.organizationId);
  applyHeader('workspaceid', context.workspaceId);
  applyHeader('workspaceId', context.workspaceId);
};

/**
 * UPT-2040 : en-tête qui marque un appel comme venant de l'application web. Le serveur en tire la source
 * « Formulaire » de la piste d'audit (`FORM` ; sans lui l'appel est journalisé `API`). Posé avec les en-têtes de
 * locataire, donc sur tous les appels axios et sur les clients qui appellent `buildSaasHeaders`.
 */
export const GEODE_CLIENT_HEADER = 'X-Geode-Client';
export const GEODE_CLIENT_WEB = 'web';

export const buildSaasHeaders = (context: SaasContext) => {
  const headers: Record<string, string> = { [GEODE_CLIENT_HEADER]: GEODE_CLIENT_WEB };
  if (context.tenantId) {
    headers.tenantid = context.tenantId;
    headers.tenantId = context.tenantId;
  }
  if (context.organizationId) {
    headers.organizationid = context.organizationId;
    headers.organizationId = context.organizationId;
  }
  if (context.workspaceId) {
    headers.workspaceid = context.workspaceId;
    headers.workspaceId = context.workspaceId;
  }
  return headers;
};

export const syncSaasContext = (options: ResolveOptions = {}) => {
  const context = resolveSaasContext(options);
  persistSaasContext(context);
  applySaasHeaders(context);
  return context;
};

/**
 * Remove workspace/subscription headers from axios without touching tenant/org headers.
 * Call this immediately after a tenant switch so that API calls fired before
 * refreshAuthContext() returns don't carry the previous tenant's subscriptionId.
 * The correct workspaceId is restored by syncSaasContext({ authContext: nextContext })
 * once the refresh completes.
 */
export const clearSaasWorkspaceHeaders = () => {
  for (const key of ['workspaceId', 'workspaceid', 'subscriptionId', 'subscriptionid']) {
    delete (axios.defaults.headers.common as Record<string, unknown>)[key];
  }
  if (typeof window !== 'undefined') {
    localStorage.removeItem('workspaceId');
    localStorage.removeItem('workspaceid');
    localStorage.removeItem('subscriptionId');
    localStorage.removeItem('subscriptionid');
  }
};

export const clearSaasContext = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('tenantid');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('organizationid');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('workspaceId');
    localStorage.removeItem('workspaceid');
    localStorage.removeItem('subscriptionId');
    localStorage.removeItem('subscriptionid');
  }
  const keysToDelete = [
    'tenantid', 'tenantId',
    'organizationid', 'organizationId',
    'workspaceid', 'workspaceId',
    'subscriptionid', 'subscriptionId',
  ];
  for (const key of keysToDelete) {
    delete axios.defaults.headers.common[key];
  }
};
